import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { Badge } from '../../components/ui/badge';
import { Activity, Eye, RefreshCw, Filter, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

const ExternalEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providerFilter, setProviderFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [retryLoading, setRetryLoading] = useState(null);
  const { toast } = useToast();

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (providerFilter) params.append('provider', providerFilter);
      if (eventTypeFilter) params.append('event_type', eventTypeFilter);
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      const queryString = params.toString();
      const url = `/admin/external-events${queryString ? `?${queryString}` : ''}`;
      const response = await api.get(url);
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao carregar eventos externos',
      });
    } finally {
      setLoading(false);
    }
  }, [providerFilter, eventTypeFilter, startDate, endDate, toast]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const fetchEventDetail = async (eventId) => {
    try {
      const response = await api.get(`/admin/external-events/${eventId}`);
      setSelectedEvent(response.data);
      setDialogOpen(true);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: 'Erro ao buscar detalhes do evento',
      });
    }
  };

  const handleRetry = async (eventId) => {
    setRetryLoading(eventId);
    try {
      await api.post(`/admin/external-events/${eventId}/retry`);
      toast({
        title: 'Evento reprocessado!',
        description: 'O evento foi marcado para reprocessamento.',
      });
      fetchEvents();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro',
        description: error.response?.data?.detail || 'Erro ao reprocessar evento',
      });
    } finally {
      setRetryLoading(null);
    }
  };

  const clearFilters = () => {
    setProviderFilter('');
    setEventTypeFilter('');
    setStartDate('');
    setEndDate('');
  };

  const hasActiveFilters = providerFilter || eventTypeFilter || startDate || endDate;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getStatusBadge = (processedAt) => {
    return processedAt ? (
      <Badge variant="default" className="bg-green-600">Processado</Badge>
    ) : (
      <Badge variant="secondary">Pendente</Badge>
    );
  };

  const getEventTypeBadge = (eventType) => {
    const config = {
      PIX_RECEIVED: { color: 'bg-green-500/20 text-green-600 dark:text-green-400', label: 'Pix Recebido' },
      PIX_SENT: { color: 'bg-blue-500/20 text-blue-600 dark:text-blue-400', label: 'Pix Enviado' },
      PIX_RETURN: { color: 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400', label: 'Pix Devolvido' },
      BOLETO_PAID: { color: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400', label: 'Boleto Pago' },
      BOLETO_EXPIRED: { color: 'bg-red-500/20 text-red-600 dark:text-red-400', label: 'Boleto Vencido' },
      WEBHOOK: { color: 'bg-purple-500/20 text-purple-600 dark:text-purple-400', label: 'Webhook' },
    };
    const { color, label } = config[eventType] || { color: 'bg-gray-500/20 text-gray-600 dark:text-gray-400', label: eventType };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>{label}</span>;
  };

  const getProviderBadge = (provider) => {
    const config = {
      BB: { color: 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400', label: 'Banco do Brasil' },
      INTERNAL: { color: 'bg-blue-500/20 text-blue-600 dark:text-blue-400', label: 'Interno' },
    };
    const { color, label } = config[provider] || { color: 'bg-gray-500/20 text-gray-600', label: provider };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${color}`}>{label}</span>;
  };

  return (
    <div className="space-y-6" data-testid="external-events-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">Eventos Externos</h1>
        <p className="text-muted-foreground mt-2">Webhooks e notificações de parceiros (Banco do Brasil)</p>
      </div>

      {/* Filters */}
      <Card className="border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtros
            </CardTitle>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                <X className="mr-2 h-4 w-4" />
                Limpar Filtros
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Provider</Label>
              <Select value={providerFilter || 'ALL'} onValueChange={(val) => setProviderFilter(val === 'ALL' ? '' : val)}>
                <SelectTrigger data-testid="filter-provider">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  <SelectItem value="BB">Banco do Brasil</SelectItem>
                  <SelectItem value="INTERNAL">Interno</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipo de Evento</Label>
              <Select value={eventTypeFilter || 'ALL'} onValueChange={(val) => setEventTypeFilter(val === 'ALL' ? '' : val)}>
                <SelectTrigger data-testid="filter-event-type">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos</SelectItem>
                  <SelectItem value="PIX_RECEIVED">Pix Recebido</SelectItem>
                  <SelectItem value="PIX_SENT">Pix Enviado</SelectItem>
                  <SelectItem value="PIX_RETURN">Pix Devolvido</SelectItem>
                  <SelectItem value="BOLETO_PAID">Boleto Pago</SelectItem>
                  <SelectItem value="BOLETO_EXPIRED">Boleto Vencido</SelectItem>
                  <SelectItem value="WEBHOOK">Webhook</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Data Início</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Data Fim</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="filter-end-date"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Events List */}
      <Card className="border-border/50">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Lista de Eventos</CardTitle>
              <CardDescription>{events.length} evento(s) encontrado(s)</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchEvents} className="rounded-full">
              <RefreshCw className="mr-2 h-4 w-4" />
              Atualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            </div>
          ) : events.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum evento encontrado</p>
              {hasActiveFilters && (
                <p className="text-sm mt-2">Tente ajustar os filtros</p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {events.map((event) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors"
                  data-testid={`event-${event.id}`}
                >
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm text-muted-foreground">#{event.id}</span>
                      {getProviderBadge(event.provider)}
                      {getEventTypeBadge(event.event_type)}
                      {getStatusBadge(event.processed_at)}
                    </div>
                    <div className="flex gap-4 text-sm text-muted-foreground flex-wrap">
                      <span>Event ID: <span className="font-mono">{event.provider_event_id}</span></span>
                      <span>Criado: {formatDate(event.created_at)}</span>
                      {event.processed_at && (
                        <span>Processado: {formatDate(event.processed_at)}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="rounded-full"
                      onClick={() => fetchEventDetail(event.id)}
                      data-testid={`view-payload-${event.id}`}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Ver Payload
                    </Button>
                    {!event.processed_at && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="rounded-full"
                        onClick={() => handleRetry(event.id)}
                        disabled={retryLoading === event.id}
                        data-testid={`retry-${event.id}`}
                      >
                        <RefreshCw className={`h-4 w-4 ${retryLoading === event.id ? 'animate-spin' : ''}`} />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event Detail Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              Detalhes do Evento #{selectedEvent?.id}
            </DialogTitle>
            <DialogDescription>
              {selectedEvent && (
                <span className="flex items-center gap-2 mt-2">
                  {getProviderBadge(selectedEvent.provider)}
                  {getEventTypeBadge(selectedEvent.event_type)}
                  {getStatusBadge(selectedEvent.processed_at)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {selectedEvent && (
            <div className="flex-1 overflow-auto space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Provider Event ID</p>
                  <p className="font-mono text-sm break-all">{selectedEvent.provider_event_id}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Org ID</p>
                  <p className="text-sm">{selectedEvent.org_id}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Criado em</p>
                  <p className="text-sm">{formatDate(selectedEvent.created_at)}</p>
                </div>
                {selectedEvent.processed_at && (
                  <div>
                    <p className="text-sm text-muted-foreground">Processado em</p>
                    <p className="text-sm">{formatDate(selectedEvent.processed_at)}</p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Payload (JSON)</Label>
                <div className="rounded-lg bg-muted/50 border border-border/50 p-4 max-h-72 overflow-auto">
                  <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                    {JSON.stringify(selectedEvent.payload, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex justify-between sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
            >
              Fechar
            </Button>
            {selectedEvent && !selectedEvent.processed_at && (
              <Button
                onClick={() => {
                  handleRetry(selectedEvent.id);
                  setDialogOpen(false);
                }}
                className="rounded-full"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Reprocessar Evento
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExternalEvents;
